import useTrackEvent from '@/composables/mixpanel/useTrackEvent';
import { eventNames } from '@/constants/eventNames';
import api from '@/services/axios';
import { useDebounceFn } from '@vueuse/core';
import { ref } from 'vue';
import { useAppStore } from '../appStore';

export const createBaseStore = (resourceName) => {
  const appStore = useAppStore();
  const items = ref([]);
  const debouncedUpdates = new Map();
  const pagination = ref({
    total: 0,
    next_page: null,
    prev_page: null,
    next_url: null,
    prev_url: null,
    page: null,
    per_page: 25,
    sort_by: 'last_modified_desc',
  });
  const cache = ref(new Map());

  const setPagination = (response) => {
    const { next, previous } = response || {};

    let nextUrl = next ? new URL(next) : null;
    let previousUrl = previous ? new URL(previous) : null;

    const nextPage = next && nextUrl ? parseInt(nextUrl.searchParams.get('page')) : 1;
    const previousPage = previous && previousUrl ? parseInt(previousUrl.searchParams.get('page')) || 0 : 0;

    const currentPage = nextPage ? nextPage - 1 : previousPage ? previousPage + 1 : 1;

    pagination.value = {
      total: response?.count ?? 0,
      next_url: next ? next : null,
      prev_url: previous ? previous : null,
      next_page: nextPage,
      prev_page: previousPage,
      per_page: 25,
      page: currentPage,
      sort_by: pagination.value.sort_by ?? 'last_modified_desc',
    };
  };
  const fetchItems = async (itemId = null, overrideUrl, query_param = {}) => {
    if (itemId && cache.value.has(itemId)) {
      return cache.value.get(itemId);
    }

    const queryString = new URLSearchParams(query_param).toString() || '';
    appStore.setLoading(true);
    try {
      const url = overrideUrl
        ? overrideUrl
        : itemId
          ? `/${resourceName}/${itemId}/?${queryString}`
          : `/${resourceName}/?${queryString}`;

      const response = await api.get(url);
      setPagination(response.data);
      if (itemId) {
        const item = response.data;

        if (item) {
          cache.value.set(itemId, item);
          return item;
        }
        return null;
      } else {
        useTrackEvent({
          eventName: eventNames.resource.selected,
          label: `User selecting ${resourceName}`,
          sendImmediately: true,
        });

        const itemsList = response.data.results || [];

        items.value = itemsList;
        itemsList.forEach((item) => {
          if (item && item.code) {
            cache.value.set(item.code, item);
          }
        });
        return itemsList;
      }
    } catch (error) {
      console.error(`Error fetching ${resourceName}:`, error);

      return { error: error.response || {} };
    } finally {
      appStore.setLoading(false);
    }
  };

  const storeItem = async (credentials, overrideUrl) => {
    appStore.setLoading(true);
    try {
      const response = await api.post(`/${resourceName}/`, credentials);
      useTrackEvent({
        eventName: eventNames.resource.created,
        label: `User creating ${resourceName}`,
        data: credentials,
        sendImmediately: true,
      });
      addItemToStore(response.data);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${resourceName}:`, error);
      return { error: error.response || {} };
    } finally {
      appStore.setLoading(false);
    }
  };

  const destroyItem = async (id) => {
    appStore.setLoading(true);
    try {
      await api.delete(`/${resourceName}/${id}/`);
      useTrackEvent({
        eventName: eventNames.resource.deleted,
        label: `User deleting ${resourceName}`,
        sendImmediately: true,
      });
      removeItemFromStore(id);
    } catch (error) {
      console.error(`Error deleting ${resourceName}:`, error);
      return { error: error.response || {} };
    } finally {
      appStore.setLoading(false);
    }
  };

  const getDebouncedUpdate = (id) => {
    if (!debouncedUpdates.has(id)) {
      debouncedUpdates.set(
        id,
        useDebounceFn(async (data) => {
          appStore.setLoading(true);
          try {
            const response = await api.put(`/${resourceName}/${id}/`, data);
            return response.data;
          } catch (error) {
            throw error;
          } finally {
            appStore.setLoading(false);
          }
        }, 500)
      );
    }
    return debouncedUpdates.get(id);
  };

  const updateItem = async (id, data) => {
    try {
      // Update the store immediately for responsive UI
      updateItemInStore({ code: id, ...data });

      // Make debounced API call (per-ID so different modules don't cancel each other)
      return await getDebouncedUpdate(id)(data);
    } catch (error) {
      console.error(`Error updating ${resourceName}:`, error);
      return { error: error.response || {} };
    }
  };

  const addItemToStore = (item) => {
    updateItemInStore(item);
  };

  const removeItemFromStore = (id) => {
    items.value = items.value.filter((item) => item.code !== id);
    cache.value.delete(id);
    debouncedUpdates.delete(id);
  };

  const updateItemInStore = (updateItem) => {
    const index = items.value.findIndex((item) => item.code === updateItem.code);
    if (index !== -1) {
      items.value[index] = updateItem;
    } else {
      items.value.push(updateItem);
    }
    cache.value.set(updateItem.code, updateItem);
  };

  return {
    pagination,
    items,
    fetchItems,
    storeItem,
    destroyItem,
    updateItem,
    addItemToStore,
    removeItemFromStore,
    updateItemInStore,
  };
};
